/**
 * Triage dialog for a persona-agent finding. Opened from the Findings table when the
 * admin changes a finding's status; captures the lifecycle move plus optional
 * attribution (source = product vs harness/infra, human severity, disposition
 * substatus, and a free-text comment) in one explicit Save. Every save appends to the
 * finding's triageLog, so the "why" trail is preserved — shown read-only below the form.
 *
 * See docs/FeatureDesigns/persona-agents.md ("Finding triage model").
 */
import { useState } from 'react';
import { format } from 'date-fns';
import { AGENT_SEVERITIES, AGENT_SEVERITY_LABELS } from '@padcanvas/shared';
import { Modal, Badge } from '@shared-ui/components';
import { adminApi, type AgentFeedback, type FindingSource, type FindingSubStatus } from '../../api/adminApi';
import { SOURCES, SUBSTATUSES, SOURCE_LABEL, SOURCE_VARIANT } from './findingSource';

const STATUSES = ['new', 'triaged', 'resolved', 'dismissed'];

export function TriageDialog({
  finding,
  initialStatus,
  onClose,
  onSaved,
}: {
  finding: AgentFeedback;
  initialStatus?: string;      // status the admin just picked in the table, if any
  onClose: () => void;
  onSaved: () => void;
}) {
  const [status, setStatus] = useState(initialStatus ?? finding.status ?? 'new');
  const [source, setSource] = useState<FindingSource | ''>(finding.source ?? '');
  const [triageSeverity, setTriageSeverity] = useState<string>(finding.triageSeverity ? String(finding.triageSeverity) : '');
  const [substatus, setSubstatus] = useState<FindingSubStatus | ''>(finding.substatus ?? '');
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await adminApi.triageAgentFinding(finding.id, finding.personaId, {
        status,
        source: source || undefined,
        triageSeverity: triageSeverity ? Number(triageSeverity) : undefined,
        substatus: substatus || undefined,
        comment: comment.trim() || undefined,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save triage');
      setSaving(false);
    }
  };

  const log = finding.triageLog ?? [];

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Triage finding"
      size="md"
      footer={
        <div className="pa-triage-foot">
          {error && <span className="pa-triage-err">{error}</span>}
          <button className="pa-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="pa-btn pa-btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      }
    >
      <div className="pa-triage">
        <p className="pa-triage-obs">
          <span className="pa-mono">{finding.personaId}</span> · {finding.category}
          {finding.operation ? ` · ${finding.operation}` : ''}
          <br />
          {finding.observation}
        </p>

        <div className="pa-triage-grid">
          <label className="pa-triage-field">
            <span>Status</span>
            <select className="pa-input" value={status} onChange={e => setStatus(e.target.value)}>
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>

          <label className="pa-triage-field">
            <span>Source</span>
            <select className="pa-input" value={source} onChange={e => setSource(e.target.value as FindingSource | '')}>
              <option value="">— unset —</option>
              {SOURCES.map(s => <option key={s} value={s}>{SOURCE_LABEL[s]}</option>)}
            </select>
          </label>

          <label className="pa-triage-field">
            <span>Severity (human)</span>
            <select className="pa-input" value={triageSeverity} onChange={e => setTriageSeverity(e.target.value)}>
              <option value="">— use agent's ({finding.severity}) —</option>
              {AGENT_SEVERITIES.map(n => <option key={n} value={n}>{n} ({AGENT_SEVERITY_LABELS[n]})</option>)}
            </select>
          </label>

          <label className="pa-triage-field">
            <span>Substatus</span>
            <select className="pa-input" value={substatus} onChange={e => setSubstatus(e.target.value as FindingSubStatus | '')}>
              <option value="">— none —</option>
              {SUBSTATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        </div>

        <label className="pa-triage-field">
          <span>Comment (context for later)</span>
          <textarea
            className="pa-input pa-triage-comment"
            rows={3}
            placeholder="e.g. Not a product bug — the harness clicked the wrong element; toolbelt resolution issue."
            value={comment}
            onChange={e => setComment(e.target.value)}
          />
        </label>

        {log.length > 0 && (
          <div className="pa-triage-history">
            <div className="pa-triage-history-title">History</div>
            {[...log].reverse().map((e, i) => (
              <div key={i} className="pa-triage-history-row">
                <div className="pa-triage-history-meta">
                  <Badge variant="default" size="sm">{e.status}</Badge>
                  {e.source && <Badge variant={SOURCE_VARIANT[e.source]} size="sm">{SOURCE_LABEL[e.source]}</Badge>}
                  {e.triageSeverity != null && <span className="pa-triage-history-sev">sev {e.triageSeverity}</span>}
                  {e.substatus && <span className="pa-triage-history-sub">{e.substatus}</span>}
                  <span className="pa-triage-history-when">
                    {e.at ? format(new Date(e.at), 'MMM d HH:mm') : ''} · {e.by}
                  </span>
                </div>
                {e.comment && <div className="pa-triage-history-comment">{e.comment}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
